import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface OGImageUploadProps {
  currentImageUrl: string | null;
  onImageChange: (url: string) => void;
}

export function OGImageUpload({ currentImageUrl, onImageChange }: OGImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validação de tipo
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        variant: 'destructive',
        title: 'Tipo de arquivo inválido',
        description: 'Por favor, envie uma imagem JPG, PNG ou WEBP',
      });
      return;
    }

    // Validação de tamanho (2MB máximo)
    const maxSize = 2 * 1024 * 1024; // 2MB em bytes
    if (file.size > maxSize) {
      toast({
        variant: 'destructive',
        title: 'Arquivo muito grande',
        description: 'A imagem deve ter no máximo 2MB',
      });
      return;
    }

    setIsUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Gerar nome único para o arquivo
      const fileExt = file.name.split('.').pop();
      const fileName = `og-image-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      // Upload para bucket seo-images
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('seo-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Obter URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('seo-images')
        .getPublicUrl(uploadData.path);

      onImageChange(publicUrl);

      toast({
        title: 'Imagem carregada',
        description: 'Não esqueça de clicar em "Salvar Configurações" para persistir',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro no upload',
        description: error.message,
      });
    } finally {
      setIsUploading(false);
      // Limpar o input
      event.target.value = '';
    }
  };

  const handleRemove = () => {
    onImageChange('');
    toast({
      title: 'Imagem removida',
      description: 'Não esqueça de salvar as configurações',
    });
  };

  return (
    <div className="space-y-4">
      {currentImageUrl ? (
        <div className="relative group">
          <div className="relative aspect-[1.91/1] w-full max-w-md border rounded-lg overflow-hidden bg-muted">
            <img
              src={currentImageUrl}
              alt="Open Graph preview"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <label htmlFor="og-image-input" className="cursor-pointer">
                <Button type="button" variant="secondary" size="sm" asChild>
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    Trocar
                  </span>
                </Button>
              </label>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleRemove}
              >
                <X className="w-4 h-4 mr-2" />
                Remover
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <label
          htmlFor="og-image-input"
          className="cursor-pointer block"
        >
          <div className="relative aspect-[1.91/1] w-full max-w-md border-2 border-dashed rounded-lg hover:border-primary transition-colors bg-muted/30 flex flex-col items-center justify-center gap-2">
            {isUploading ? (
              <>
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Fazendo upload...</p>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-muted-foreground" />
                <div className="text-center">
                  <p className="text-sm font-medium">Clique para fazer upload</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG ou WEBP (máx. 2MB)
                  </p>
                </div>
              </>
            )}
          </div>
        </label>
      )}

      <input
        id="og-image-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleImageUpload}
        disabled={isUploading}
        className="hidden"
      />

      <div className="text-xs text-muted-foreground space-y-1">
        <p>✓ Dimensões ideais: <strong>1200x630 pixels</strong> (proporção 1.91:1)</p>
        <p>✓ Formatos: JPG, PNG ou WEBP</p>
        <p>✓ Tamanho máximo: 2MB</p>
      </div>
    </div>
  );
}
